<template>
	<div class="container py-4">
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/inventory/checks"
				title="Back to inventory checks"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div class="flex-grow-1">
				<h1 class="mb-0">Inventory Check #{{ checkId }}</h1>
				<p v-if="check" class="text-muted mb-0">
					Started <TimeRelativeWithTooltip :time="check.created" />
					<span v-if="check.completed">
						· completed <TimeRelativeWithTooltip :time="check.completed" />
					</span>
					<span v-else>· open</span>
				</p>
			</div>
			<span
				v-if="check"
				class="badge rounded-pill fs-6"
				:class="check.completed ? 'bg-success' : 'bg-warning text-dark'"
			>
				{{ check.completed ? "Complete" : "Open" }}
			</span>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<template v-else-if="check">
			<div v-if="check.note" class="card shadow-sm mb-4">
				<div class="card-body">
					<div class="info-label">Note</div>
					<div>{{ check.note }}</div>
				</div>
			</div>

			<!-- Count entry: the counted amount for a product at a location. The
			     ledger's expected amount is read at the moment of counting, so a
			     difference becomes an adjustment. -->
			<div v-if="!check.completed" class="card shadow-sm mb-4">
				<div class="card-body">
					<h2 class="h5 mb-3">Record a Counted Line</h2>
					<form class="row g-2 align-items-end" @submit.prevent="submitCount">
						<div class="col-12 col-md-4">
							<label class="form-label" for="countProduct">Product</label>
							<select
								id="countProduct"
								v-model="countForm.product_catalog_id"
								class="form-select"
								required
							>
								<option value="" disabled>Choose a product</option>
								<option
									v-for="product in catalog"
									:key="product.product_id"
									:value="product.product_id"
								>
									{{ product.name }}
								</option>
							</select>
						</div>
						<div class="col-12 col-md-3">
							<label class="form-label" for="countLocation">Location</label>
							<select
								id="countLocation"
								v-model="countForm.storage_location_id"
								class="form-select"
								required
							>
								<option :value="null" disabled>Choose a location</option>
								<option
									v-for="location in activeLocations"
									:key="location.id"
									:value="location.id"
								>
									{{ location.path || location.name }}
								</option>
							</select>
						</div>
						<div class="col-6 col-md-2">
							<label class="form-label" for="countAmount">Counted</label>
							<input
								id="countAmount"
								v-model.number="countForm.counted_amount"
								type="number"
								min="0"
								step="any"
								class="form-control"
								required
							/>
						</div>
						<div class="col-12 col-md-3">
							<label class="form-label" for="countReason">Reason</label>
							<input
								id="countReason"
								v-model="countForm.reason"
								type="text"
								class="form-control"
								placeholder="If it differs from the ledger"
							/>
						</div>
						<div class="col-12">
							<div v-if="expectedHint" class="text-muted small">
								The ledger expects
								{{ formatAmount(expectedHint.amount) }}
								{{ expectedHint.unit ?? "" }} at this location.
							</div>
						</div>
						<div class="col-12 col-md-auto">
							<ButtonLoading
								type="submit"
								text="Record count"
								icon="bi bi-plus-lg"
								variant="primary"
								:loading="savingCount"
								:disabled="!countValid"
							/>
						</div>
					</form>
				</div>
			</div>

			<div class="card shadow-sm">
				<div class="card-body">
					<h2 class="h5 mb-3">Counted Lines</h2>

					<div v-if="lines.length === 0" class="text-center py-4 text-muted">
						No lines counted yet.
					</div>

					<div v-else class="table-responsive">
						<table class="table table-striped align-middle">
							<thead class="table-light">
								<tr>
									<th scope="col">Product</th>
									<th scope="col">Location</th>
									<th scope="col" class="text-end">Counted</th>
									<th scope="col" class="text-end">Expected</th>
									<th scope="col" class="text-end">Variance</th>
									<th scope="col">Reason</th>
									<th scope="col">Counted at</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="line in lines"
									:key="line.id"
									:class="varianceClass(line)"
								>
									<td>{{ line.product_name }}</td>
									<td>{{ line.location_name }}</td>
									<td class="text-end">
										{{ formatAmount(line.counted_amount) }}
									</td>
									<td class="text-end">
										{{ formatAmount(line.expected_amount) }}
									</td>
									<td class="text-end fw-semibold">
										{{
											formatVariance(line.counted_amount - line.expected_amount)
										}}
									</td>
									<td class="text-muted">{{ line.reason || "—" }}</td>
									<td>
										<TimeRelativeWithTooltip :time="line.created" />
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<div v-if="!check.completed" class="mt-3">
						<ButtonLoading
							text="Complete check"
							icon="bi bi-check2-circle"
							variant="primary"
							:loading="completing"
							:disabled="lines.length === 0"
							@click="completeCheck"
						/>
						<div class="text-muted small mt-2">
							Completing closes the check; no further lines can be recorded.
						</div>
					</div>
				</div>
			</div>
		</template>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute } from "vue-router";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	CheckCount,
	InventoryCheckDetail,
	InventoryCheckLine,
	InventoryLocation,
	InventoryStock,
	PesticideCatalogProduct,
} from "@/type/api";

const route = useRoute();
// The route passes no props; the check id is the path segment (contract §8).
const checkId = parseInt(route.params.id as string, 10);

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const detail = ref<InventoryCheckDetail | null>(null);
const loading = ref(false);
const error = ref("");

const catalog = ref<PesticideCatalogProduct[]>([]);
const locations = ref<InventoryLocation[]>([]);
// Stock is read only to show the expected amount beside the count form; the
// authoritative expected amount is the server's at the moment of counting.
const stock = ref<InventoryStock[]>([]);

const check = computed(() => detail.value?.check ?? null);
const lines = computed(() => detail.value?.lines ?? []);
const activeLocations = computed(() =>
	[...locations.value]
		.filter((location) => !location.retired)
		.sort((a, b) => a.path.localeCompare(b.path)),
);

function formatAmount(value: number): string {
	if (Number.isInteger(value)) return String(value);
	return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

function formatVariance(value: number): string {
	if (value === 0) return "0";
	const magnitude = formatAmount(Math.abs(value));
	return value > 0 ? `+${magnitude}` : `-${magnitude}`;
}

// A line whose count disagrees with the ledger is the one worth seeing.
function varianceClass(line: InventoryCheckLine): string {
	const variance = line.counted_amount - line.expected_amount;
	if (variance < 0) return "table-danger";
	if (variance > 0) return "table-warning";
	return "";
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		detail.value = await apiClient.JSONGet<InventoryCheckDetail>(
			`/api/inventory/check/${checkId}`,
		);
	} catch (e: any) {
		error.value = e?.message || "Failed to load this inventory check.";
		showNotification("Failed to load check", error.value);
	} finally {
		loading.value = false;
	}
}

async function loadReferences() {
	try {
		// The two inventory collections the count form chooses from, through the
		// generic QUERY endpoints (the legacy GET lists' replacement,
		// doc/in-progress/query.md §11 Phase 4), each walked whole; the check
		// detail read above and the shared catalog below are untouched.
		const [catalogData, locationPage, stockPage] = await Promise.all([
			apiClient.JSONGet<{ products: PesticideCatalogProduct[]; total: number }>(
				"/api/pesticide-catalog",
			),
			apiClient.JSONQueryAll<InventoryLocation>("/api/inventory/location"),
			apiClient.JSONQueryAll<InventoryStock>("/api/inventory/stock"),
		]);
		catalog.value = catalogData.products ?? [];
		locations.value = locationPage.items;
		stock.value = stockPage.items;
	} catch (e: any) {
		showNotification(
			"Failed to load products",
			e?.message || "Product and location choices may be incomplete.",
		);
	}
}

// ---- Count entry ----------------------------------------------------------

const countForm = reactive<{
	product_catalog_id: string;
	storage_location_id: number | null;
	counted_amount: number;
	reason: string;
}>({
	product_catalog_id: "",
	storage_location_id: null,
	counted_amount: 0,
	reason: "",
});
const savingCount = ref(false);

const countValid = computed(
	() =>
		countForm.product_catalog_id !== "" &&
		countForm.storage_location_id !== null &&
		countForm.counted_amount >= 0,
);

const expectedHint = computed(() => {
	if (
		countForm.product_catalog_id === "" ||
		countForm.storage_location_id === null
	) {
		return null;
	}
	return (
		stock.value.find(
			(row) =>
				row.product_catalog_id === countForm.product_catalog_id &&
				row.storage_location_id === countForm.storage_location_id,
		) ?? null
	);
});

async function submitCount() {
	if (!countValid.value) return;
	savingCount.value = true;
	const reason = countForm.reason.trim();
	const body: CheckCount = {
		product_catalog_id: countForm.product_catalog_id,
		storage_location_id: countForm.storage_location_id as number,
		counted_amount: countForm.counted_amount,
		...(reason !== "" ? { reason } : {}),
	};
	try {
		await apiClient.JSONPost<InventoryCheckLine>(
			`/api/inventory/check/${checkId}/count`,
			body,
		);
		countForm.counted_amount = 0;
		countForm.reason = "";
		await load();
		showNotification(
			"Count recorded",
			"The line is added; any difference became an adjustment.",
		);
	} catch (e: any) {
		showNotification(
			"Failed to record count",
			e?.message || "Please try again.",
		);
	} finally {
		savingCount.value = false;
	}
}

const completing = ref(false);

async function completeCheck() {
	completing.value = true;
	try {
		await apiClient.JSONPost<unknown>(
			`/api/inventory/check/${checkId}/complete`,
		);
		await load();
		showNotification("Check completed", "No further lines can be recorded.");
	} catch (e: any) {
		showNotification(
			"Failed to complete check",
			e?.message || "Please try again.",
		);
	} finally {
		completing.value = false;
	}
}

onMounted(() => {
	load();
	loadReferences();
});
</script>

<style scoped>
.info-label {
	font-size: 0.8rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #6c757d;
	margin-bottom: 0.25rem;
}

.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
